/**
 * SkillPointsRatio Component
 * 
 * A clean component for displaying skill points in a simple x/y ratio format.
 * Provides clear visual distinction between the actual skill points earned
 * and the total available skill points.
 * 
 * @param skillSpecificPoints - Points earned for this specific skill
 * @param totalSkillPoints - Total points available across all skills
 * @param skillName - Name of the skill (optional, for calculation modal)
 * @param category - Skill category (optional, for calculation modal)
 * @param calculationType - Type of calculation context (optional, for calculation modal)
 * @param skillsByCategory - Skills data (optional, for calculation modal)
 * @param activityId - Activity ID (optional, for calculation modal)
 * @param activities - Activities data (optional, for calculation modal)
 * @param projectTitle - Project title (optional, for project-specific calculation modal)
 * @param enhancedProjects - Enhanced projects with GitHub data (optional, for combined calculation modal)
 * @param className - Optional additional CSS classes
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SkillCalculationModal from '../modals/SkillCalculationModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LiveCalculationExample from '../../ui/LiveCalculationExample';
import { generateSpecificSkillCalculation } from '@/lib/skills';

interface SkillPointsRatioProps {
  skillSpecificPoints: number;
  totalSkillPoints: number;
  skillName?: string;
  category?: 'profession' | 'lead' | 'soft' | 'language' | 'tools';
  calculationType?: 'professional' | 'activity' | 'company' | 'project' | 'private' | 'combined';
  skillsByCategory?: any;
  activityId?: number;
  activities?: any[];
  projectTitle?: string;
  enhancedProjects?: any[];
  className?: string;
}

export function SkillPointsRatio({ 
  skillSpecificPoints, 
  totalSkillPoints,
  skillName,
  category,
  calculationType = 'professional',
  skillsByCategory,
  activityId,
  activities,
  projectTitle,
  enhancedProjects,
  className = '' 
}: SkillPointsRatioProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Round values for display
  const displaySkillPoints = Math.round(skillSpecificPoints * 10) / 10;
  const displayTotalPoints = Math.round(totalSkillPoints * 10) / 10;

  // Generate calculation data if we have skill details
  const calculationData = skillName && category && skillsByCategory 
    ? generateSpecificSkillCalculation(
        skillName,
        category,
        calculationType,
        skillsByCategory,
        activityId,
        activities,
        projectTitle,
        enhancedProjects
      )
    : null;

  const handleClick = (e: React.MouseEvent) => {
    if (skillName && category && skillsByCategory && calculationData) {
      e.stopPropagation();
      e.preventDefault();
      console.log('Opening modal manually');
      setIsModalOpen(true);
    }
  };

  const ratioContent = (
    <div 
      className={`flex items-baseline gap-1 ${className} ${calculationData ? 'cursor-pointer hover:opacity-75 transition-opacity' : ''}`}
      onClick={handleClick}
    >
      <motion.span 
        className="text-2xl font-bold text-primary"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        {displaySkillPoints}
      </motion.span>
      <span className="text-lg text-muted-foreground font-medium">/</span>
      <motion.span 
        className="text-xl font-semibold text-muted-foreground"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        {displayTotalPoints}
      </motion.span>
    </div>
  );

  // Return the ratio with our own modal
  return (
    <>
      {ratioContent}
      {calculationData && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] bg-gray-900 border-gray-700 text-gray-100 overflow-hidden">
            <DialogHeader>
              <DialogTitle className="text-gray-100">
                Skill Calculation Details: {skillName}
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-4">
              <div className="text-sm text-gray-300 mb-4">
                This shows exactly how the {calculationData.finalResult}% level for "{skillName}" was calculated 
                in the {calculationType === 'professional' ? 'professional overview' : 
                       calculationType === 'activity' ? 'activity context' : 
                       calculationType === 'company' ? 'company context' : 
                       calculationType === 'private' ? 'private project context' :
                       calculationType === 'combined' ? 'combined skills context' : 'project context'}.
              </div>
              
              <LiveCalculationExample
                skillName={calculationData.skillName}
                calculationType={calculationType}
                totalProjects={calculationData.totalProjects}
                projectsWithSkill={calculationData.projectsWithSkill}
                totalDuration={calculationData.totalDuration}
                projects={calculationData.projects}
                totalSkillPoints={calculationData.totalSkillPoints}
                skillSpecificPoints={calculationData.skillSpecificPoints}
                finalResult={calculationData.finalResult}
                activityPeriod={calculationData.activityPeriod}
                allProjectDurations={calculationData.allProjectDurations}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}